import {
  Badge,
  Card,
  Col,
  Collapse,
  Descriptions,
  Row,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import * as Sentry from "@sentry/browser";
import {
  Activity,
  Company,
  CompanyContact,
  Contribution,
  Entitlement,
  Opportunity,
  PaginatedResourcePageSelected,
  Payment,
  RedemptionsRequest,
  Schedule,
  TagRecord,
  Ticket,
  User,
} from "../helpers/shared-interfaces";
import CompanyActivityList from "./CompanyActivityList";
import { NavLink, RouteComponentProps } from "react-router-dom";
import React, { Fragment } from "react";
import { AxiosResponse } from "axios";
import {
  COMPANY_PAYMENTS,
  COMPANY_SCHEDULES,
  GET_COMPANY_CONTACT_PERSONS,
  GET_COMPANY_CONTRIBUTIONS,
  GET_COMPANY_INTERACTIONS_QUERY,
  GET_COMPANY_QUERY,
  GET_COMPANY_REDEMPTION_REQUESTS,
  GET_COMPANY_TAGS,
  GET_COMPANY_TICKETS,
} from "../gql/companies";
import {
  formatSubscriptionTags,
  injectKey,
  mapSubscriptionToContribution,
  request,
} from "../helpers/utilities";

import TagsSection from "../components/TagsSection";
import TagsManagement from "../components/TagsManagement";
import ContactDetailsView from "../components/ContactDetailsView";
import PaymentAccountsView from "../components/PaymentsAccountsView";
import {
  ContributionsTableFactory,
  DocumentTableFactory,
  generateCompanyContributionColumns,
  paymentColumns,
  scheduleColumns,
  ticketColumns,
  transformDocumentsData,
} from "./ViewCompany";
import { RedemptionsRequestsTableFactory } from "./ViewCustomer";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { tabPaneStyle } from "../helpers/constants";
import ActivitiesSection from "../components/ActivitiesSection";
import { FormInstance } from "antd/lib/form";
import { generateDisplayErrorMessage } from "../helpers/utilities";


const { Panel } = Collapse;

interface Props
  extends RouteComponentProps<
    RouteParams,
    any,
    { type: string; requests?: Company[]; }
  > {
  entitlement: Entitlement;
  user: User;
  companyInteractionId?: string;
}

interface RouteParams {
  id: string;
}

interface State {
  id: string | number;
  isLoading: boolean;
  company?: Company;
  activities: Activity[];
  contributions: Contribution[];
  contributionsLoader: boolean;
  tickets: Ticket[];
  companies: Company[];
  payments: Payment[];
  schedules: Schedule[];
  hasLoadedContributions: string[];
  redemptionRequests: RedemptionsRequest[];
  activitiesCount: number;
  activitiesPage: number;
  areInteractionsLoading: boolean;
  contactPersons?: CompanyContact[];
  currentCompanyToProcess: number;
  currentEmployeesPage: number;
  formerEmployeesPage: number;
  phoenixTickets: Ticket[];
  allCompanyTags: TagRecord[];
  isLoadingCompanyTags: boolean;
  contributionsPagination: {
    fundCode: string;
    page: number;
    total: number;
  }[];
}

export const mapSegmentToColor: Record<string, string> = {
  red: "#e00404",
  blue: "#0763f7",
  amber: "#ffbf00",
  green: "#00ff00",
};

export const ArchivedDetails = (props: {
  company: Company;
  contactPersons: CompanyContact[] | undefined;
  contributions: Contribution[];
  currentEmployeesPage: number;
  paginatedResourceNextPage: (
    page: number,
    paginatedResource: PaginatedResourcePageSelected,
  ) => void;
}): JSX.Element => {
  return (
    <Fragment>
      <Row className="view-opportunity-row">
        <Descriptions
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ marginRight: "50px" }}>Bio Information</div>
            </div>
          }
        >
          {props.company.petraId && (
            <Descriptions.Item label={<b>Petra ID</b>} key="Petra ID">
              {props.company.petraId}{" "}
            </Descriptions.Item>
          )}

          <Descriptions.Item label={<b>Company Name</b>} key="Company Name">
            {props.company.name}{" "}
          </Descriptions.Item>

          {props.company.tinNumber && (
            <Descriptions.Item label={<b>TIN</b>} key="TIN">
              {props.company.tinNumber}
            </Descriptions.Item>
          )}

          {props.company.trustee && (
            <Descriptions.Item label={<b>Former Trustee</b>} key="Trustee">
              {props.company.trustee.name}
            </Descriptions.Item>
          )}

          {props.company.industrySector && (
            <Descriptions.Item
              label={<b>Industry Sector</b>}
              key="Industry Sector"
            >
              {props.company.industrySector.name}
            </Descriptions.Item>
          )}

          {props.company.companySegment && (
            <Descriptions.Item
              label={<b>Company Segment</b>}
              key="Company Segment"
            >
              <span>
                {props.company.companySegment}
              </span>
            </Descriptions.Item>
          )}

          {props.company.numberOfEmployees && (
            <Descriptions.Item
              label={<b>Number of Employees</b>}
              key="Number Of Employees"
            >
              {props.company.numberOfEmployees}
            </Descriptions.Item>
          )}

          {props.company.relationshipManager && (
            <Descriptions.Item
              label={<b>Relationship Manager</b>}
              key="Relationship Manager"
            >
              {props.company.relationshipManager.name}
            </Descriptions.Item>
          )}

          {props.company?.mergedTo && (
            <Descriptions.Item label={<b>Merged To</b>} key="mergedTo">
              <NavLink
                to={`/companies/${props.company.mergedTo.id}`}
                key={props.company.mergedTo.id}
              >
                {props.company.mergedTo.name}
              </NavLink>
            </Descriptions.Item>
          )}

          {props.company?.ssnitNumber && (
            <Descriptions.Item label={<b>SSNIT Number</b>} key="ssnitNumber">
              {props.company.ssnitNumber}
            </Descriptions.Item>
          )}

          {props.company?.rocNumber && (
            <Descriptions.Item label={<b>RDG/ROC Number</b>} key="rocNumber">
              {props.company.rocNumber}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Row>

      {props.company.tags.length > 0 && (
        <TagsSection tags={props.company.tags} />
      )}

      <ContactDetailsView
        phones={props.company.companyPhones}
        emails={props.company.companyEmails}
        adresses={props.company.companyAddresses}
        formerEmployees={{
          data: props.company?.formerEmployees?.results ?? [],
          totalCount: props.company?.formerEmployees
            ? props.company?.formerEmployees?.count
            : 0,
        }}
        currentEmployees={{
          data: props.company?.currentEmployees?.results ?? [],
          totalCount: props.company?.currentEmployees
            ? props.company?.currentEmployees?.count
            : 0,
        }}
        paginatedResourceNextPage={props?.paginatedResourceNextPage}
        style={{ marginBottom: "30px", marginTop: "50px" }}
        companyContacts={props.company.companyContacts}
        directors={props.company.directors}
      />
    </Fragment>
  );
};

export default class ViewArchivedCompany extends React.Component<Props, State> {
  reference: React.RefObject<FormInstance>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.reference = React.createRef<FormInstance>();
    this.state = {
      payments: [],
      schedules: [],
      company: undefined,
      isLoading: true,
      id: this.props.match.params.id,
      contributions: [],
      contributionsLoader: false,
      activities: [],
      tickets: [],
      companies: [],
      hasLoadedContributions: [],
      redemptionRequests: [],
      activitiesCount: 0,
      activitiesPage: 1,
      areInteractionsLoading: false,
      contactPersons: [],
      currentCompanyToProcess: 0,
      currentEmployeesPage: 1,
      formerEmployeesPage: 1,
      phoenixTickets: [],
      allCompanyTags: [],
      isLoadingCompanyTags: false,
      contributionsPagination: [],
    };
  }

  async paginatedResourceNextPage(
    page: number,
    paginatedResponse: PaginatedResourcePageSelected,
  ): Promise<void> {
    switch (paginatedResponse) {
      case PaginatedResourcePageSelected.currentEmployees:
        this.setState({ isLoading: true, currentEmployeesPage: page }, () => {
          this.getCompany();
        });
        break;

      case PaginatedResourcePageSelected.formerEmployees:
        this.setState({ isLoading: true, formerEmployeesPage: page }, () => {
          this.getCompany();
        });
        break;

      default:
        break;
    }
  }

  async componentDidMount(): Promise<void> {
    this.setState(
      {
        id: this.props.match.params.id,
        currentCompanyToProcess:
          this.props?.location?.state?.requests?.findIndex(
            (item) =>
              item?.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      () => {
        this.getCompany();
        this.getCompanyContactPersons();
        this.getCompanyInteractions();
        this.fetchCompanyTags();
      },
    );
  }

  redirectToArchivedCompaniesPage = (): void => {
    this.props.history.push("/archived-companies");
  };

  getCompany = (): void => {
    const {
      id: companyId,
      currentEmployeesPage,
      formerEmployeesPage,
    } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_COMPANY_QUERY,
      variables: {
        companyId,
        associationNameOne: "current_employees",
        customerPageOne: currentEmployeesPage,
        associationNameTwo: "former_employees",
        customerPageTwo: formerEmployeesPage,
      },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          company: Company;
        };
      }>): void => {
        if (data.data.company) {
          this.setState({
            company: data.data.company,
            isLoading: false,
          });
        } else {
          this.redirectToArchivedCompaniesPage();
        }
      },
    );
  };

  getCompanyContactPersons = (): void => {
    const { id: companyId } = this.state;

    request({
      query: GET_COMPANY_CONTACT_PERSONS,
      variables: { companyId, page: "1" },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            companyContactPersons: {
              count: number;
              results: CompanyContact[];
              errors: object;
            };
          };
        }>): void => {
          if (data.data.companyContactPersons.results) {
            this.setState({
              contactPersons: data.data.companyContactPersons.results,
            });
          }
        },
      )
      .catch((error) => {
        message.error("Could Not Fetch Company Contacts");
        Sentry.captureException(error);
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  };

  fetchCompanyTags = async (): Promise<void> => {
    const { id: companyId } = this.state;

    this.setState({ isLoadingCompanyTags: true });
    try {
      const {
        data: { data },
      }: AxiosResponse<{
        data: {
          company: {
            allCompanyTags: TagRecord[];
          };
        };
      }> = await request({
        query: GET_COMPANY_TAGS,
        variables: { companyId },
      });

      this.setState({ allCompanyTags: data.company.allCompanyTags ?? [] });
    } catch (error) {
      message.error(`Could Not Fetch Company Tags - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoadingCompanyTags: false });
    }
  };

  assignCompanyTags = async (): Promise<void> => undefined;

  getCompanyInteractions = (): void => {
    const { id: companyId, activitiesPage } = this.state;
    this.setState({ areInteractionsLoading: true });
    request({
      query: GET_COMPANY_INTERACTIONS_QUERY,
      variables: { companyId, page: activitiesPage.toString() },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            interactionsForCompany: {
              count: number;
              results: Activity[];
            };
          };
        }>): void => {
          this.setState({
            activities: data.data.interactionsForCompany.results,
            activitiesCount: data.data.interactionsForCompany.count,
          });
        },
      )
      .finally((): void => {
        this.setState({ areInteractionsLoading: false });
      });
  };

  interactionsNextPage = async (page: number): Promise<void> => {
    this.setState({ activitiesPage: page }, this.getCompanyInteractions);
  };

  viewInteraction = (currentInteractionId: string): void => {
    this.props.history.push(`/notification-records/${currentInteractionId}`);
  };

  noop = (): void => undefined;

  noopAsync = async (): Promise<void> => undefined;

  getContributionData = async (key: string | string[]): Promise<void> => {
    if (typeof key === "string") {
      await this.getCompanyContributions(key, 1);
    }
  };

  async contributionsNextPage(page: number, fundCode: string): Promise<void> {
    let { contributionsPagination: oldPaginationInfo } = this.state;
    const newPaginationInfo = oldPaginationInfo.map((item) => {
      if (item.fundCode === fundCode) {
        return { ...item, page };
      } else {
        return { ...item };
      }
    });
    this.setState(
      { isLoading: true, contributionsPagination: newPaginationInfo },
      () => {
        this.getCompanyContributions(
          fundCode,
          oldPaginationInfo.find((item) => item.fundCode === fundCode)?.page ??
          1,
        );
      },
    );
  }

  getCompanyPayments = (): void => {
    interface Response {
      data: {
        companyPayments: Payment[];
      };
    }

    this.setState({ isLoading: true });
    const petraId = this.state.company?.petraId;

    request({
      query: COMPANY_PAYMENTS,
      variables: { petraId: petraId || "" },
    })
      .then((response: AxiosResponse<Response>): void => {
        this.setState({
          payments: response.data.data.companyPayments,
        });
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  };

  getSchedulesPaymentsRequestsData = async (
    key: string | string[],
  ): Promise<void> => {
    if (key === "payments") await this.getCompanyPayments();
    if (key === "schedules") await this.getSchedules();
    if (key === "redemptionRequests") await this.getCompanyRedemptions();
    if (key === "phoenixTickets") await this.getPhoenixTickets();
  };

  getCompanyRedemptions = (): void => {
    const { id: companyId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_COMPANY_REDEMPTION_REQUESTS,
      variables: { companyId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          company: Company;
        };
      }>): void => {
        if (data.data.company) {
          this.setState({
            redemptionRequests: data.data.company.redemptionRequests,
            isLoading: false,
          });
        } else {
          this.redirectToArchivedCompaniesPage();
        }
      },
    );
  };

  getFundCodeInformation = (key: string) => {
    return this.state.contributionsPagination.find(
      (item) => item.fundCode === key,
    );
  };

  getSchedules = (): void => {
    interface Response {
      data: {
        companySchedules: Schedule[];
      };
    }

    this.setState({ isLoading: true });
    const petraId = this.state.company?.petraId;

    request({
      query: COMPANY_SCHEDULES,
      variables: { petraId: petraId || "" },
    })
      .then((response: AxiosResponse<Response>): void => {
        this.setState({
          schedules: response.data.data.companySchedules,
        });
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  };

  getPhoenixTickets = (): void => {
    const { id: companyId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_COMPANY_TICKETS,
      variables: { companyId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          company: Company;
        };
      }>): void => {
        if (data.data.company) {
          this.setState({
            phoenixTickets: data.data?.company?.tickets ?? [],
            isLoading: false,
          });
        }
      },
    );
  };

  getCompanyContributions = (fundCode: string, page: number): void => {
    const {
      company,
      hasLoadedContributions,
      contributions,
      contributionsPagination,
    } = this.state;

    const currentFundCodePaginationInfo = this.getFundCodeInformation(fundCode);

    if (
      hasLoadedContributions.includes(fundCode) &&
      currentFundCodePaginationInfo?.page === page
    )
      return;

    this.setState({ contributionsLoader: true });
    request({
      query: GET_COMPANY_CONTRIBUTIONS,
      variables: {
        petraId: company?.petraId,
        fundCode,
        page: currentFundCodePaginationInfo?.page ?? 1,
      },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            clientContributions: {
              count: number;
              results: Contribution[];
            };
          };
        }>): void => {
          if (data.data.clientContributions) {
            const newPagination = currentFundCodePaginationInfo
              ? contributionsPagination.map((item) => {
                if (item.fundCode === fundCode) {
                  return {
                    ...item,
                    total: data.data.clientContributions.count,
                  };
                } else {
                  return { ...item };
                }
              })
              : [
                {
                  fundCode,
                  page: 1,
                  total: data.data.clientContributions.count,
                },
              ];

            this.setState({
              contributions: [
                ...contributions.filter((item) => item.shareClass !== fundCode),
                ...data.data.clientContributions.results,
              ],
              isLoading: false,
              contributionsPagination: newPagination,
            });
          } else {
            this.redirectToArchivedCompaniesPage();
          }
        },
      )
      .finally((): void => {
        let newHasLoadedContributions = hasLoadedContributions;
        newHasLoadedContributions.push(fundCode);

        this.setState({
          contributionsLoader: false,
          hasLoadedContributions: newHasLoadedContributions,
        });
      });
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      company,
      contributions,
      contributionsLoader,
      payments,
      schedules,
      redemptionRequests,
      contactPersons,
      currentEmployeesPage,
      phoenixTickets,
      allCompanyTags,
      isLoadingCompanyTags,
    } = this.state;
    let userInterface: JSX.Element;
    const hasSubscription =
      company &&
      company.companySubscriptions &&
      company.companySubscriptions.length > 0;

    const subscriptionsWithContributions = mapSubscriptionToContribution(
      contributions,
      company?.companySubscriptions,
    );

    if (company) {
      userInterface = (
        <Fragment>
          <ArchivedDetails
            company={company}
            contactPersons={contactPersons}
            contributions={contributions}
            currentEmployeesPage={currentEmployeesPage}
            paginatedResourceNextPage={this.paginatedResourceNextPage}
          />

          {company.companyPayoutAccounts.length > 0 && (
            <PaymentAccountsView
              payoutAccounts={company.companyPayoutAccounts}
              debitAccounts={[]}
            />
          )}

          <div style={{ marginTop: "45px" }}>
            <Spin spinning={contributionsLoader}>
              <Descriptions title={<div>Contributions </div>}></Descriptions>
              <Collapse
                accordion
                style={{
                  marginBottom: "12px",
                }}
                onChange={this.getContributionData}
              >
                {hasSubscription &&
                  subscriptionsWithContributions.map((item) => {
                    const currentFundCodePage = this.getFundCodeInformation(
                      item.subscription?.product?.fundCode ?? "",
                    );

                    return (
                      <Panel
                        header={<span>{item.subscription.product.name}</span>}
                        key={item.subscription?.product?.fundCode ?? ""}
                      >
                        <Row>
                          {
                            <Col
                              span={16}
                              key={item.subscription.companyVan?.id}
                            >
                              <h3>
                                VAN:{" "}
                                <Tag> {item.subscription.companyVan?.van} </Tag>
                              </h3>
                              <h3>
                                Institution:
                                <Tag>
                                  {" "}
                                  {
                                    item.subscription.companyVan
                                      ?.financialInstitution.name
                                  }
                                </Tag>{" "}
                              </h3>
                            </Col>
                          }
                        </Row>
                        {item.subscription.companySubscriptionTags.length >
                          0 && (
                            <Row
                              style={{
                                marginTop: "10px",
                                marginBottom: "10px",
                              }}
                            >
                              <TagsSection
                                tags={formatSubscriptionTags(
                                  item.subscription.companySubscriptionTags,
                                )}
                              />
                            </Row>
                          )}

                        {ContributionsTableFactory(
                          item.clientContributions,
                          generateCompanyContributionColumns(
                            item.clientContributions[0],
                          ),
                          this.contributionsNextPage,
                          currentFundCodePage?.page ?? 1,
                          currentFundCodePage?.total ?? 10,
                          item.subscription?.product?.fundCode ?? "",
                        )}
                      </Panel>
                    );
                  })}
              </Collapse>
            </Spin>
          </div>

          <div
            style={{
              marginTop: "40px",
            }}
          >
            <Descriptions
              title={<div>Schedules, Payments, Requests</div>}
            ></Descriptions>

            <Collapse
              style={{
                marginBottom: "12px",
              }}
              accordion={true}
              onChange={this.getSchedulesPaymentsRequestsData}
            >
              <Panel key="redemptionRequests" header="Redemption Requests">
                {RedemptionsRequestsTableFactory(redemptionRequests)}
              </Panel>
              <Panel key="payments" header="Payments">
                <Table
                  bordered={true}
                  columns={paymentColumns}
                  dataSource={injectKey(payments, "id")}
                  pagination={{
                    defaultPageSize: 10,
                    total: payments.length,
                  }}
                />
              </Panel>
              <Panel key="schedules" header="Schedules">
                <Table
                  bordered={true}
                  columns={scheduleColumns}
                  dataSource={injectKey(schedules, "id")}
                  pagination={{
                    defaultPageSize: 10,
                    total: schedules.length,
                  }}
                />
              </Panel>
            </Collapse>
          </div>

          <div
            style={{
              marginTop: "40px",
            }}
          >
            <Descriptions title={<div>Tickets</div>}></Descriptions>

            <Collapse
              style={{
                marginBottom: "12px",
              }}
              accordion={true}
              onChange={this.getSchedulesPaymentsRequestsData}
            >
              <Panel key="phoenixTickets" header="Tickets">
                <Table
                  bordered={true}
                  columns={ticketColumns}
                  dataSource={injectKey(phoenixTickets, "id")}
                  pagination={{
                    defaultPageSize: 10,
                    total: schedules.length,
                  }}
                />
              </Panel>
            </Collapse>
          </div>

          <div
            style={{
              marginTop: "40px",
            }}
          >
            <Spin spinning={this.state.areInteractionsLoading}>
              <ActivitiesSection
                showBorder={true}
                showRequests={false}
                isInteractionLoading={this.state.areInteractionsLoading}
                forCustomer={false}
                forCompany={true}
                showHeading={true}
                showInteraction={false}
                toggleInteractionEdit={this.noop}
                toggleViewRequestsSection={this.noop}
                toggleViewInteractionSection={this.viewInteraction}
                toggleViewRedemptionRequestsSection={this.noop}
                reference={this.reference}
                toggleCreateTicket={this.noop}
                toggleCreateInteractionModal={this.noop}
                createInteraction={this.noopAsync}
                hasAttachment={true}
                attachments={[]}
                updateAttachments={this.noop}
                removeAttachment={this.noop}
                activities={this.state.activities as Activity[]}
                showForm={true}
                useBaseColumn={false}
                transferRequests={[]}
                customer={undefined}
                company={company}
                activitiesCount={this.state.activitiesCount}
                nextPage={this.interactionsNextPage}
                disableCreateInteractionButton={true}
                isReadOnly={true}
              />
            </Spin>
          </div>

          <div
            style={{
              marginTop: "40px",
            }}
          >
            <Descriptions title={<div>Tags</div>}></Descriptions>

            <Collapse
              defaultActiveKey={["tags"]}
              style={{
                marginBottom: "12px",
              }}
            >
              <Panel
                key="tags"
                header={
                  <span>
                    Company Tags
                    <Badge
                      count={
                        allCompanyTags.filter((tag) => tag.isActive).length
                      }
                      style={tabPaneStyle}
                    />
                  </span>
                }
              >
                <TagsManagement
                  tagRecords={allCompanyTags}
                  tagScope={{ forCompany: true, forAccount: true }}
                  supportsEffectiveDate
                  isSubmitting={isLoadingCompanyTags}
                  onAssignTags={this.assignCompanyTags}
                  isReadOnly={true}
                />
              </Panel>
            </Collapse>
          </div>

          <div
            style={{
              marginTop: "40px",
            }}
          >
            <Descriptions title={<div>Company Activities</div>}></Descriptions>

            <Collapse
              defaultActiveKey={["companyActivities"]}
              style={{
                marginBottom: "12px",
              }}
            >
              <Panel key="companyActivities" header="Company Activities">
                <Spin spinning={this.state.areInteractionsLoading}>
                  <EntitledSection
                    entitlement={entitlements.CompanyActivities.list}
                  >
                    <CompanyActivityList
                      companyId={company?.id}
                      {...this.props}
                      isReadOnly={true}
                    />
                  </EntitledSection>
                </Spin>
              </Panel>
            </Collapse>
          </div>

          {company.opportunities && company.opportunities.length > 0 && (
            <div>
              <Descriptions
                style={{
                  marginTop: "30px",
                }}
                title={<div>Client Documents</div>}
              ></Descriptions>
              <Collapse
                style={{
                  marginBottom: "12px",
                }}
              >
                {company.opportunities &&
                  company.opportunities.map(
                    (opportunity: Opportunity): JSX.Element => {
                      const documents = transformDocumentsData(opportunity);
                      return (
                        <Panel
                          header={
                            <span>
                              {opportunity.product && opportunity.product.name}
                            </span>
                          }
                          key={opportunity.id as string}
                        >
                          {DocumentTableFactory(documents)}
                        </Panel>
                      );
                    },
                  )}
              </Collapse>
            </div>
          )}
        </Fragment>
      );
    } else if (isLoading) {
      userInterface = (
        <div
          style={{
            minHeight: "240px",
          }}
        />
      );
    } else {
      userInterface = <Fragment />;
    }

    return (
      <Spin spinning={isLoading}>
        <Row>
          <Card
            style={{
              overflow: "hidden",
            }}
          >
            <Row
              style={{
                marginBottom: "20px",
              }}
            >
              <Col span={24}>{userInterface}</Col>
            </Row>
          </Card>
        </Row>
      </Spin>
    );
  }
}
